import Vue from 'vue';

/**
 * Logs a message to console, if application is running in development mode.
 *
 * @param msg message to log
 * @param {string} level optional log level ('error', 'info' or 'warning')
 */
Vue.prototype.$log = function (msg, level) {
  // noinspection JSIncompatibleTypesComparison
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  if (level === 'error') {
    console.error(msg);
  } else if (level === 'info') {
    console.info(msg);
  } else if (level === 'warning') {
    console.warn(msg);
  } else {
    console.log(msg);
  }
};

/**
 * Checks, if the user is currently authorized.
 *
 * @returns {boolean} true, if a user session is available, otherwise false
 */
Vue.prototype.$isAuthorized = function () {
  return this.$session.exists();
};

/**
 * If the user is currently authorized, he is redirected to an internal page.
 *
 * @returns {boolean} true, if a redirection happened, otherwise false
 */
Vue.prototype.$redirectIfAuthorized = function () {
  if (this.$isAuthorized()) {
    this.$log('User is already logged in.');
    this.$router.push({ name: 'events' });
    return false;
  }
  return true;
};

/**
 * Checks, if the user is logged in and has a certain permission.
 *
 * @param {string} permission optional permission name - if not set, no permission check happens
 * @returns {boolean} true, if the user is authorized and has the provided permission (if set)
 */
Vue.prototype.$hasAccess = function (permission) {
  if (!this.$isAuthorized()) {
    return false;
  }

  if (permission !== undefined) {
    const permissions = this.$session.get('permissions');
    if (!permissions.includes(permission)) {
      return false;
    }
  }

  return true;
};

/**
 * Check, if the user is logged in and has access to the management pages.
 */
Vue.prototype.$hasAreaAccess = function (permissionArea) {
  if (!this.$isAuthorized()) {
    return false;
  }

  const permissions = this.$session.get('permissions');
  // eslint-disable-next-line no-restricted-syntax
  for (const p of permissions) {
    if (p.startsWith(permissionArea)) return true;
  }
  return false;
};

/**
 * Checks, if the user is logged in and has a certain permission, and redirects in case of
 * insufficient privileges.
 *
 * @param {string} permission optional permission name - if not set, no permission check happens
 * @returns {boolean} true, if the user is authorized and has the provided permission (if set)
 */
Vue.prototype.$checkAccess = function (permission) {
  const hasAccess = this.$hasAccess(permission);

  if (!hasAccess) {
    if (!this.$isAuthorized()) {
      this.$log('User is not logged in.');
      this.$router.push({ name: 'login' });
    } else {
      this.$log(`User doesn't have "${permission}" permission.`);
      this.$router.push({ name: 'noAccess' });
    }
    return false;
  }

  return true;
};

/**
 * Returns the official_id of the current session
 *
 * @returns {int} session official id
 */

Vue.prototype.$official_id = function () {
  return this.$session.get('official_id');
};

/**
 * Array of travel options
 *
 * @returns {array} travel options
 */
Vue.prototype.$travelOptions = [
  { value: null, text: 'Choose option' },
  { value: 'car', text: 'Car' },
  { value: 'rental car', text: 'Rental car' },
  { value: 'train', text: 'Train' },
  { value: 'airplane', text: 'Airplane' },
  { value: 'ride', text: 'Ride' },
  { value: 'local transport', text: 'Local transport' },
  { value: 'none', text: 'None' },
];

/**
 * Concatenates the name of a participant and returns it
 * @param participant
 * @returns {string}
 */
Vue.prototype.$getFullName = function (participant) {
  const fn = participant.official.first_name;
  const mn = participant.official.middle_name || '';
  const ln = participant.official.last_name || '';
  let name = '';
  if (fn) name += `${fn} `;
  if (mn) name += `${mn} `;
  if (ln) name += ln;
  return name.trim();
};

/**
 * An array of all available certifications
 * @type {string[]}
 */
Vue.prototype.$certifications = ['referee', 'judge', 'inspector', 'timekeeper', 'scorekeeper', 'cutmen', 'doctor', 'supervisor'];

/**
 * Return a sorting function, to sort participants by their first names
 * @param a
 * @param b
 * @returns {number}
 */
Vue.prototype.$sortName = function (a, b) {
  if (a.official.first_name > b.official.first_name) return 1;
  if (a.official.first_name < b.official.first_name) return -1;
  return 0;
};

/**
 * Returns true if Official has not provided approach, departure or accommodation_request
 * @param participant
 * @returns {boolean}
 */
Vue.prototype.$missingData = function (participant) {
  // eslint-disable-next-line no-restricted-syntax
  for (const t of participant.travels) {
    if (!t.type && !t.ride_travel_id) return true;
  }
  if (participant.accommodation_requested === null) return true;
  return false;
};

Vue.prototype.$formattedTime = function (value) {
  const minutes = Math.floor(value / 60);
  const seconds = value % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};
